import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Loader2, RefreshCw, Search, CalendarX, AlertCircle, Plus, Percent, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { format } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_STYLE = {
  active:   { label: 'Active',   className: 'bg-green-500/15 text-green-500 border-green-500/40' },
  inactive: { label: 'Inactive', className: 'bg-muted text-muted-foreground border-border' },
  expired:  { label: 'Expired',  className: 'bg-destructive/10 text-destructive border-destructive/40' },
};

function formatDiscount(c, currencySymbol) {
  if (c.discount_type === 'percentage') return `${c.discount_value}% off`;
  return `${currencySymbol}${c.discount_value} off`;
}

function formatUsage(c) {
  if (c.usage_limit) return `${c.usage_count} / ${c.usage_limit} uses`;
  return `${c.usage_count} uses`;
}

function formatExpiry(c) {
  if (!c.expiry_date) return 'No expiry';
  try { return format(new Date(c.expiry_date), 'MMM d, yyyy'); } catch { return c.expiry_date; }
}

export default function CouponCodes() {
  const [data, setData] = useState({ summary: { total: 0, active: 0, total_uses: 0 }, coupons: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');

  // Create-coupon modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const emptyForm = {
    event_id: '',
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    usage_limit: '',
    expiry_date: '',
  };
  const [form, setForm] = useState(emptyForm);

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';  // no easily-confused chars
    let s = '';
    for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  };

  const openCreate = async () => {
    setForm({ ...emptyForm, code: generateRandomCode() });
    setCreateOpen(true);
    if (eventsLoaded) return;
    try {
      const res = await axios.get(`${API_URL}/api/events/organizer/my-events`);
      // Keep only future / not-archived events (sensible scope for new coupons)
      const list = (res.data || []).filter(e => !e.is_archived);
      setEvents(list);
      setEventsLoaded(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load your events');
    }
  };

  const submitCreate = async () => {
    // Client-side validation (mirrors backend rules — friendlier UX)
    if (!form.event_id) { toast.error('Pick an event'); return; }
    const code = form.code.trim().toUpperCase();
    if (!code) { toast.error('Coupon code is required'); return; }
    const value = parseFloat(form.discount_value);
    if (!value || value <= 0) { toast.error('Discount must be greater than 0'); return; }
    if (form.discount_type === 'percentage' && value > 100) {
      toast.error('Percentage discount cannot exceed 100');
      return;
    }
    const limit = form.usage_limit === '' ? null : parseInt(form.usage_limit, 10);
    if (limit !== null && (Number.isNaN(limit) || limit < 1)) {
      toast.error('Usage limit must be 1 or greater');
      return;
    }

    setCreating(true);
    try {
      const body = {
        event_id: form.event_id,
        code,
        discount_type: form.discount_type,
        discount_value: value,
      };
      if (limit !== null) body.usage_limit = limit;
      if (form.expiry_date) {
        // datetime-local → ISO; treat as UTC for backend storage
        body.expiry_date = new Date(form.expiry_date).toISOString();
      }
      await axios.post(`${API_URL}/api/coupons`, body);
      toast.success(`Coupon ${code} created`);
      setCreateOpen(false);
      setForm(emptyForm);
      await fetchData(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create coupon');
    } finally { setCreating(false); }
  };

  const fetchData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/coupons/mine`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load coupons');
    } finally { if (showSpinner) setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/coupons/mine`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load coupons');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleActive = async (coupon) => {
    if (busyId) return;  // prevent double-clicks
    const target = !coupon.is_active;
    setBusyId(coupon.coupon_id);
    try {
      await axios.put(
        `${API_URL}/api/coupons/${encodeURIComponent(coupon.coupon_id)}`,
        { is_active: target }
      );
      toast.success(target ? 'Coupon reactivated' : 'Coupon deactivated');
      await fetchData(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.coupons;
    return data.coupons.filter(c =>
      (c.code || '').toLowerCase().includes(q) ||
      (c.event_title || '').toLowerCase().includes(q)
    );
  }, [data.coupons, query]);

  return (
    <div className="space-y-4" data-testid="coupon-codes-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Tag className="w-5 h-5" /> Coupon Codes
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={openCreate} data-testid="coupon-new-btn">
                <Plus className="w-4 h-4 mr-1" />
                New Coupon
              </Button>
              <Button size="sm" variant="outline" onClick={() => fetchData(true)} disabled={loading} data-testid="coupon-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Refresh
              </Button>
            </div>
          </CardTitle>
          <CardDescription data-testid="coupon-summary">
            <strong>{data.summary.total}</strong> total code{data.summary.total !== 1 ? 's' : ''} ·{' '}
            <strong>{data.summary.active}</strong> active ·{' '}
            <strong>{data.summary.total_uses}</strong> uses across all codes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or event…"
              className="pl-9"
              data-testid="coupon-search-input"
            />
          </div>

          {loading ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading coupons…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm" data-testid="coupon-empty-state">
              {data.coupons.length === 0
                ? 'No coupon codes yet. Create one from any event\u2019s edit page.'
                : 'No coupons match your search.'}
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Coupon Code</th>
                    <th className="text-left p-3">Event</th>
                    <th className="text-left p-3">Discount</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Usage</th>
                    <th className="text-left p-3">Expiry</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-testid="coupon-table-body">
                  {filtered.map(c => {
                    const style = STATUS_STYLE[c.computed_status] || STATUS_STYLE.active;
                    const isBusy = busyId === c.coupon_id;
                    const canToggle = c.computed_status !== 'expired';
                    return (
                      <tr key={c.coupon_id} data-testid={`coupon-row-${c.coupon_id}`}>
                        <td className="p-3 font-mono font-semibold">{c.code}</td>
                        <td className="p-3 max-w-[220px] truncate">{c.event_title || '—'}</td>
                        <td className="p-3">{formatDiscount(c, '₦')}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={style.className} data-testid={`coupon-status-${c.coupon_id}`}>
                            {style.label}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs" data-testid={`coupon-usage-${c.coupon_id}`}>{formatUsage(c)}</td>
                        <td className="p-3 text-xs">
                          {c.expiry_date && c.computed_status === 'expired' ? (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <CalendarX className="w-3 h-3" /> {formatExpiry(c)}
                            </span>
                          ) : (
                            formatExpiry(c)
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {canToggle ? (
                            <Button
                              size="sm"
                              variant={c.is_active ? 'outline' : 'default'}
                              onClick={() => toggleActive(c)}
                              disabled={isBusy}
                              data-testid={`coupon-toggle-${c.coupon_id}`}
                            >
                              {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                              {c.is_active ? 'Deactivate' : 'Reactivate'}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Cannot reuse
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create-coupon modal */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setForm(emptyForm); }}>
        <DialogContent className="max-w-lg" data-testid="coupon-create-dialog">
          <DialogHeader>
            <DialogTitle>Create a new coupon</DialogTitle>
            <DialogDescription>
              Each coupon belongs to a single event. Codes are case-insensitive and must be unique per event.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="coupon-event-select">Event</Label>
              <Select value={form.event_id} onValueChange={(v) => setForm({ ...form, event_id: v })}>
                <SelectTrigger id="coupon-event-select" data-testid="coupon-event-select">
                  <SelectValue placeholder={events.length === 0 ? 'Loading…' : 'Select an event'} />
                </SelectTrigger>
                <SelectContent>
                  {events.map(e => (
                    <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="coupon-code-input">Coupon Code</Label>
              <div className="flex gap-2">
                <Input
                  id="coupon-code-input"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="font-mono"
                  placeholder="SAVE10"
                  data-testid="coupon-code-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, code: generateRandomCode() })} data-testid="coupon-regenerate-btn">
                  Regenerate
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Discount Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={form.discount_type === 'percentage' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm({ ...form, discount_type: 'percentage' })}
                    className="flex-1"
                    data-testid="coupon-type-percentage"
                  >
                    <Percent className="w-3 h-3 mr-1" /> %
                  </Button>
                  <Button
                    type="button"
                    variant={form.discount_type === 'fixed' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm({ ...form, discount_type: 'fixed' })}
                    className="flex-1"
                    data-testid="coupon-type-fixed"
                  >
                    <DollarSign className="w-3 h-3 mr-1" /> Fixed
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="coupon-value-input">
                  Discount Value {form.discount_type === 'percentage' ? '(%)' : ''}
                </Label>
                <Input
                  id="coupon-value-input"
                  type="number"
                  min="1"
                  step="0.01"
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  placeholder={form.discount_type === 'percentage' ? '10' : '500'}
                  data-testid="coupon-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="coupon-limit-input">Usage Limit</Label>
                <Input
                  id="coupon-limit-input"
                  type="number"
                  min="1"
                  value={form.usage_limit}
                  onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                  placeholder="Unlimited"
                  data-testid="coupon-limit-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-expiry-input">Expiry Date</Label>
                <Input
                  id="coupon-expiry-input"
                  type="datetime-local"
                  value={form.expiry_date}
                  onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                  data-testid="coupon-expiry-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="coupon-create-submit-btn">
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Create Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
